import React from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TableCell from 'components/table/TableCell';
import {Service} from 'types/service';
import Table from 'components/table/Table';
import {SearchType} from 'types/search';
import {ServicesThunks} from 'features/programs/program/services/servicesThunks';
import {
  setIsEditing,
  setService,
} from 'features/programs/program/services/servicesSlice';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import {setActiveTab, PROGRAM_TABS} from 'features/programs/programsSlice';
import DotsMenu from 'components/DotsMenu';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import TrashIcon from 'assets/images/icons/TrashIcon';
import usePermissions from 'hooks/usePermissions';
import CheckCircleIcon from 'assets/images/icons/CheckCircleIcon';
import XCircleIcon from 'assets/images/icons/XCircleIcon';
import {capitalizeFirstLetter} from '../../../../utils/caseConverter';

const ServicesTable: React.FC<{openDrawer: () => void}> = ({openDrawer}) => {
  const services = useSelector((state: RootState) => state.service.services);
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.service.pagy);
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const loading = useSelector(
    (state: RootState) => state.service.status === 'loading',
  );

  interface VisibleColumnsType {
    name: boolean;
    description: boolean;
    type: boolean;
    billingCode: boolean;
    modifiers: boolean;
    placeOfService: boolean;
    licenseRequired: boolean;
  }

  const [visibleColumns, setVisibleColumns] =
    React.useState<VisibleColumnsType>({
      name: true,
      description: true,
      type: true,
      billingCode: true,
      modifiers: true,
      placeOfService: true,
      licenseRequired: true,
    });

  const columnOptions = [
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {
      label: 'Description',
      value: 'description',
      isVisible: visibleColumns.description,
    },
    {label: 'Type', value: 'type', isVisible: visibleColumns.type},
    {
      label: 'Billing Code',
      value: 'billingCode',
      isVisible: visibleColumns.billingCode,
    },
    {
      label: 'Modifiers',
      value: 'modifiers',
      isVisible: visibleColumns.modifiers,
    },
    {
      label: 'Place of Service',
      value: 'placeOfService',
      isVisible: visibleColumns.placeOfService,
    },
    {
      label: 'License Required',
      value: 'licenseRequired',
      isVisible: visibleColumns.licenseRequired,
    },
  ];

  const searchByOptions = [{label: 'Name', value: 'name', operator: 'like'}];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    await dispatch(
      ServicesThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenNewServiceDrawer = () => {
    dispatch(
      setService({
        name: '',
        description: '',
        applicableState: '',
        placeOfService: '',
        defaultPoS: '',
        billingCode: '',
        billingModifier: '',
        secondaryModifier: '',
        blocksTimerEntry: '',
        licenseRequired: '',
      }),
    );
    dispatch(setIsEditing(true));
    openDrawer();
  };

  const handleDelete = (serviceId: string) => {
    dispatch(ServicesThunks.delete(serviceId) as any);
    setDotsMenuIsOpen(null);
  };

  const handleEdit = (serviceId: string) => {
    dispatch(setIsEditing(true));
    openDrawer();
    dispatch(ServicesThunks.show(serviceId) as any);
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      activeTab={'Services'}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={PROGRAM_TABS}
      searchByOptions={searchByOptions}
      onColumnToggle={handleColumnToggle}
      recordsName={'Services'}
      columnOptions={columnOptions}
      searchable={true}
      loading={loading}
      columns={true}
      blueButton={
        checkPermission('create', 'Service') ? (
          <BlueButton
            onClick={handleOpenNewServiceDrawer}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
      <thead>
        <tr>
          {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
          {visibleColumns.description && <TableHeader label="Description" />}
          {visibleColumns.type && <TableHeader label="Type" sortBy="type" />}
          {visibleColumns.billingCode && <TableHeader label="Billing Code" />}
          {visibleColumns.modifiers && <TableHeader label="Modifiers" />}
          {visibleColumns.placeOfService && (
            <TableHeader label="Place of Service" />
          )}
          {visibleColumns.licenseRequired && (
            <TableHeader label="License Required" />
          )}
          <TableHeader label="" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {services.map((service: Service) => {
          const userOptions = [];
          if (checkPermission('update', 'Service')) {
            userOptions.push({
              label: 'Edit',
              onClick: () => handleEdit(service.id),
              hoverClass: 'hover:bg-cyan-100',
            });
          }
          if (checkPermission('delete', 'Service')) {
            userOptions.push({
              label: 'Delete',
              onClick: () => handleDelete(service.id),
              icon: <TrashIcon />,
              hoverClass: 'hover:bg-red-100 hover:text-red-800',
            });
          }
          return (
            <TableRow key={service.id}>
              {visibleColumns.name && (
                <TableCell>
                  <div className=" relative group">
                    <div className="w-full flex items-center gap-x-3">
                      <div className="flex-grow">
                        <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                          {service.name}
                        </span>
                      </div>
                    </div>
                  </div>
                </TableCell>
              )}
              {visibleColumns.description && (
                <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                  <div
                    style={{maxWidth: '150px'}}
                    className="whitespace-nowrap overflow-hidden text-ellipsis">
                    {service.description}
                  </div>
                </TableCell>
              )}
              {visibleColumns.type && (
                <TableCell>
                  {capitalizeFirstLetter(service.applicableState)}
                </TableCell>
              )}
              {visibleColumns.billingCode && (
                <TableCell>{service.billingCode}</TableCell>
              )}
              {visibleColumns.modifiers && (
                <TableCell>{service.billingModifier}</TableCell>
              )}
              {visibleColumns.placeOfService && (
                <TableCell>{service.defaultPlaceOfService?.name}</TableCell>
              )}
              {visibleColumns.licenseRequired && (
                <TableCell>
                  {service.licenseRequiredForService ? (
                    <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-500" />
                  )}
                </TableCell>
              )}
              <TableCell>
                <DotsMenu options={userOptions} />
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default ServicesTable;
